import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  Slide,
  Stack,
  Typography,
  Box,
  Button,
  Select,
  InputLabel,
  MenuItem,
  Grid,
  Autocomplete,
  IconButton,
  Chip} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as CloseIcon } from "../../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ExceptIcon } from "../../../../../../../vendor/_new/icon/services/except.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as DuplicateIcon } from "../../../../../../../vendor/_new/icon/services/duplicate.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../../vendor/_new/icon/services/delete.svg";
import { ReactComponent as PlusIcon } from "../../../../../../../vendor/_new/icon/sites/plus.svg";

import { SafeTextField, GreenSelectForm } from "../../../../../../common/Greens";
import { SaveButton, CancelButton, containedButtonSx, formSaveButtonSx } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import { ExceptionSkipRuleType, ExpressionKeyField, FireWallExpressionCondition, UserType } from "../../../../../../../utils/constants";
import { getExpressionKeyTitle } from "../../../../../../../utils/constants";
import { getConditionTitle, getValidCondFlag } from "../../../../../application/rule/component";
import countries from "../../../../../application/rule/countries";
import colors from "../../../../../../../utils/colors";
import useSite from "../../../../../../../hooks/user/useSite";
import useAuth from "../../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateExpressionRuleForm } from "../../../../../../../utils/validation";
import {
  getExpressionValueHint,
  isExpressionRowStarted,
  resolveVisibleCellError,
} from "../../../../../../../utils/validation/expressionValidators";
import { ExpressionFieldMessage } from "../../../../../../common/ExpressionFieldMessages";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const KeyComponent = ({ keyStr, selectKeyStr, padx, pady, advanced, error, helperText }) => {
  const aKeys = [
    ExpressionKeyField.SOURCE_IP,
    ExpressionKeyField.HOST_NAME,
    ExpressionKeyField.USER_AGENT,
    ExpressionKeyField.HEADER,
    ExpressionKeyField.QUERY,
    ExpressionKeyField.COOKIE,
    ExpressionKeyField.URI,
    ExpressionKeyField.REFERER,
    ExpressionKeyField.METHOD,
  ];
  const aAdvancedKeys = [
    ExpressionKeyField.COUNTRY,
    ExpressionKeyField.CITY_NAME,
    ExpressionKeyField.AS_NUMBER,
    ExpressionKeyField.BOT_SCORE,
    ExpressionKeyField.JA3_FINGERPRINT,
  ];
  return (
    <GreenSelectForm error={Boolean(error)}>
      <InputLabel id="field-information">Field</InputLabel>
      <Select labelId="field-information" fullWidth IconComponent={ArrowdownIcon} value={keyStr} onChange={selectKeyStr}>
        {keyStr === ExpressionKeyField.NONE && (
          <MenuItem
            key={`_key0_${padx}_${pady}`}
            value={ExpressionKeyField.NONE}
            disabled
            sx={{ ...menuItemSx, display: "none!important" }}
          >
            Select Field ...
          </MenuItem>
        )}
        {aKeys.map((keyValue, keyIdx) => (
          <MenuItem key={`_key${keyIdx + 1}_${padx}_${pady}`} value={keyValue} sx={menuItemSx}>
            {getExpressionKeyTitle(keyValue)}
          </MenuItem>
        ))}
        {(undefined === advanced || true === advanced) &&
          aAdvancedKeys.map((keyValue, keyIdx) => (
            <MenuItem key={`_key${keyIdx + 1 + aKeys.length}_${padx}_${pady}`} value={keyValue} sx={menuItemSx}>
              {getExpressionKeyTitle(keyValue)}
            </MenuItem>
          ))}
      </Select>
    </GreenSelectForm>
  );
};

const CondComponent = ({ condStr, selectCondStr, disableMenu, disabled, padx, pady, error }) => {
  // Order should NOT be modified
  const aConditions = [
    FireWallExpressionCondition.EQUALS,
    FireWallExpressionCondition.NOT_EQUALS,
    FireWallExpressionCondition.CONTAINS,
    FireWallExpressionCondition.NOT_CONTAINS,
    FireWallExpressionCondition.GREATER_THAN,
    FireWallExpressionCondition.LESS_THAN,
    FireWallExpressionCondition.GREATER_THAN_OR_EQUALS_TO,
    FireWallExpressionCondition.LESS_THAN_OR_EQUALS_TO,
  ];
  // switch
  return (
    <GreenSelectForm error={Boolean(error)}>
      <InputLabel id="operation-information">Operator</InputLabel>
      <Select
        fullWidth
        labelId="operation-information"
        IconComponent={ArrowdownIcon}
        disabled={disabled}
        value={condStr}
        onChange={selectCondStr}
      >
        {condStr === "none" && (
          <MenuItem key={"_cond0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aConditions
          .filter((_, condIdx) => disableMenu[condIdx])
          .map((condValue, condIdx) => (
            <MenuItem key={`_cond${condIdx + 1}_${padx}_${pady}`} value={condValue} sx={menuItemSx}>
              {getConditionTitle(condValue)}
            </MenuItem>
          ))}
      </Select>
    </GreenSelectForm>
  );
};

const MethodValueComponent = ({ valueStr, selectValueStr, disabled, padx, pady, error, helperText }) => {
  const aMethods = ["GET", "POST", "HEAD", "PUT", "DELETE", "PATCH", "TRACE", "CONNECT"];
  return (
    <GreenSelectForm error={Boolean(error)}>
      <InputLabel id="method-value-information">Value</InputLabel>
      <Select
        fullWidth
        labelId="method-value-information"
        IconComponent={ArrowdownIcon}
        disabled={disabled}
        value={valueStr}
        onChange={selectValueStr}
      >
        {valueStr === "none" && (
          <MenuItem key={"_cond0_" + padx + "_" + pady} value="none" disabled sx={{ ...menuItemSx, display: "none!important" }}>
            Select Item ...
          </MenuItem>
        )}
        {aMethods.map((methodValue, methodIdx) => (
          <MenuItem key={`_cond${methodIdx + 1}_${padx}_${pady}`} value={methodValue} sx={{ menuItemSx }}>
            {methodValue}
          </MenuItem>
        ))}
      </Select>
    </GreenSelectForm>
  );
};
const CountryValueComponent = ({ valueStr, selectValueStr, disabled, error, helperText }) => {
  const getName = (code) => {
    let label;
    countries?.forEach((country) => {
      if (country?.value === code) label = country?.label;
    });
    return label;
  };
  return (
    <Autocomplete
      fullWidth
      options={countries}
      sx={{ border: `solid 1px ${error ? colors.red?.[500] || "#d32f2f" : colors.neutral[200]}`, borderRadius: "8px" }}
      autoHighlight
      disabled={disabled}
      onChange={selectValueStr}
      value={getName(valueStr)}
      renderOption={(props, option) => (
        <Box
          component="option"
          value={option.value}
          {...props}
          sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        >
          {option.label} ({option.value})
        </Box>
      )}
      renderInput={(params) => (
        <SafeTextField
          {...params}
          placeholder="Select a country"
          sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        />
      )}
    />
  );
};
const ValueComponent = ({ keyStr, valueStr, selectValueStr, onBlur, disabled, padx, pady, error, helperText }) => {
  let isNumber = false;
  switch (keyStr) {
    case ExpressionKeyField.AS_NUMBER:
    case ExpressionKeyField.BOT_SCORE:
      isNumber = true;
      break;
    default:
      isNumber = false;
      break;
  }
  const type = isNumber ? "number" : "text";
  return (
    <SafeTextField
      label="Value"
      fullWidth
      type={type}
      value={valueStr}
      disabled={disabled}
      onChange={selectValueStr}
      onBlur={onBlur}
      key={`_value_${padx}_${pady}`}
      error={Boolean(error)}
      helperText={error || helperText || ""}
    />
  );
};

/**
 * Inline picker rendered below the WAF Engine Selection dropdown when
 * the operator chose the Layerd Signature WAF (or the legacy OWASP
 * Signature option). Two-tier dropdown UX:
 *
 *   1. Tier 1 — MUI Autocomplete (multi-select) of signature categories
 *      (451 SQLI, 452 XSS, ...). Backed by `useWAFConfig.sdSigRules`
 *      which the parent loads on demand.
 *   2. Tier 2 — For each selected category, a separate Autocomplete
 *      (multi-select) listing only that category's individual sec rules.
 *      Backed by `useWAFConfig.allSdSecRules`.
 *
 * Removing a category from Tier 1 also prunes any of its signatures
 * from `skipSecruleIds` so the saved exception never contains orphan
 * IDs from a category that is no longer skipped.
 *
 * Both arrays empty + Signature WAF selected = "bypass ALL signature
 * checks for matching requests" (legacy semantic preserved). The UI
 * shows an explicit "All" chip in each dropdown when nothing specific
 * is selected (LDCP-140).
 */
const SIG_ALL_CATEGORY_ID = "__all_categories__";
const SIG_ALL_SECRULE_ID = "__all_secrules__";

/** Multi-select Autocomplete styling aligned with GreenSelectForm / WAF Engine Selection. */
const sigBypassMultiSelectAutocompleteSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    paddingTop: "22px",
    paddingBottom: "6px",
    paddingLeft: "16px",
    paddingRight: "16px",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: 0.5,
    minHeight: 56,
    height: "auto",
  },
  "& .MuiAutocomplete-input": {
    padding: "0 !important",
    minWidth: 30,
  },
  "& .MuiChip-root": {
    margin: "2px",
  },
  "& .MuiAutocomplete-popupIndicator": {
    marginTop: "-4px",
  },
};

const SignatureBypassPicker = ({
  sdSigRules,
  allSdSecRules,
  skipCategories,
  setSkipCategories,
  skipSecruleIds,
  setSkipSecruleIds,
  disabled,
}) => {
  const categories = React.useMemo(
    () => (Array.isArray(sdSigRules) ? sdSigRules : []),
    [sdSigRules],
  );
  const secRules = React.useMemo(
    () => (Array.isArray(allSdSecRules) ? allSdSecRules : []),
    [allSdSecRules],
  );

  // Index secRules by parent rule_id so each Tier-2 dropdown can pull
  // its options without scanning the whole flat list each render.
  const secRulesByCategory = React.useMemo(() => {
    const idx = {};
    for (const r of secRules) {
      const cat = r?.rule_id;
      if (!cat) continue;
      if (!idx[cat]) idx[cat] = [];
      idx[cat].push(r);
    }
    return idx;
  }, [secRules]);

  // Tier-1 option list — one entry per category. Falls back to the
  // embedded `crs_sec_rules` count when the flat list hasn't loaded yet.
  const allCategoryOption = React.useMemo(
    () => ({ ruleId: SIG_ALL_CATEGORY_ID, description: "All", count: 0 }),
    [],
  );

  const categoryOptions = React.useMemo(() => {
    return categories
      .map((cat) => {
        const id = cat?.rule_id;
        if (!id) return null;
        const count = Array.isArray(cat?.crs_sec_rules)
          ? cat.crs_sec_rules.length
          : secRulesByCategory[id]?.length || 0;
        return {
          ruleId: id,
          description: cat?.description || `Group ${id}`,
          count,
        };
      })
      .filter(Boolean);
  }, [categories, secRulesByCategory]);

  const categoryOptionsWithAll = React.useMemo(
    () => [allCategoryOption, ...categoryOptions],
    [allCategoryOption, categoryOptions],
  );

  const selectedCategoryOptions = React.useMemo(() => {
    if (skipCategories.length === 0) return [allCategoryOption];
    return categoryOptions.filter((o) => skipCategories.includes(o.ruleId));
  }, [skipCategories, categoryOptions, allCategoryOption]);

  const handleCategoriesChange = (newRuleIds) => {
    if (disabled) return;
    // Drop any sec-rules that belong to a category that just left
    // Tier 1, so we never persist orphan IDs.
    const removedCategories = skipCategories.filter((id) => !newRuleIds.includes(id));
    if (removedCategories.length > 0) {
      const orphanIds = new Set(
        removedCategories.flatMap((catId) =>
          (secRulesByCategory[catId] || []).map((s) => s?.sec_rule_id).filter(Boolean),
        ),
      );
      if (orphanIds.size > 0) {
        setSkipSecruleIds(skipSecruleIds.filter((id) => !orphanIds.has(id)));
      }
    }
    setSkipCategories(newRuleIds);
  };

  const onCategoryAutocompleteChange = (_e, newVal) => {
    if (!newVal?.length) {
      handleCategoriesChange([]);
      return;
    }
    const specific = newVal.filter((o) => o.ruleId !== SIG_ALL_CATEGORY_ID);
    if (specific.length === 0) {
      handleCategoriesChange([]);
      return;
    }
    handleCategoriesChange(specific.map((o) => o.ruleId));
  };

  const handleSecRulesChangeForCategory = (catRuleId, newSecRuleIds) => {
    if (disabled) return;
    // Preserve picks from every OTHER category; only this category's
    // subset is rewritten.
    const otherSecRuleIds = skipSecruleIds.filter((id) => {
      const sr = secRules.find((s) => s?.sec_rule_id === id);
      return sr && sr?.rule_id !== catRuleId;
    });
    setSkipSecruleIds([...otherSecRuleIds, ...newSecRuleIds]);
  };

  const bypassAllCategories = skipCategories.length === 0;
  const bothEmpty = bypassAllCategories && skipSecruleIds.length === 0;

  // When tier-1 is "All", hide per-category signature pickers — bypass applies to every category.
  const tier2CategoryIds = React.useMemo(
    () => (bypassAllCategories ? [] : skipCategories),
    [bypassAllCategories, skipCategories],
  );

  const onSecRulesAutocompleteChange = (catRuleId, newVal) => {
    if (!newVal?.length) {
      handleSecRulesChangeForCategory(catRuleId, []);
      return;
    }
    const specific = newVal.filter((s) => s?.sec_rule_id !== SIG_ALL_SECRULE_ID);
    if (specific.length === 0) {
      handleSecRulesChangeForCategory(catRuleId, []);
      return;
    }
    // Leaving global "All" — scope to this category with specific signatures.
    if (bypassAllCategories) {
      setSkipCategories([catRuleId]);
      setSkipSecruleIds(specific.map((s) => s?.sec_rule_id).filter(Boolean));
      return;
    }
    handleSecRulesChangeForCategory(
      catRuleId,
      specific.map((s) => s?.sec_rule_id).filter(Boolean),
    );
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: "8px",
        border: `solid 1px ${colors.neutral[200]}`,
        backgroundColor: "#fff",
      }}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          lineHeight: "24px",
          color: colors.neutral[1000],
          mb: 1,
        }}
      >
        Signatures to bypass
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: "18px",
          color: colors.neutral[700],
          mb: 3,
        }}
      >
        Pick the signature categories (and optionally individual
        signatures) this exception bypasses for matching requests.
      </Typography>

      {/* Tier 1 — category multi-select dropdown */}
      {categoryOptions.length === 0 ? (
        <Typography sx={ { fontSize: 12, color: colors.neutral[600], mb: 2 }}>
          Loading categories...
        </Typography>
      ) : (
        <GreenSelectForm variant="outlined" fullWidth sx={{ mb: tier2CategoryIds.length > 0 ? 3 : 2 }}>
          <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            popupIcon={<ArrowdownIcon />}
            options={categoryOptionsWithAll}
            getOptionLabel={(o) =>
              o.ruleId === SIG_ALL_CATEGORY_ID
                ? "All"
                : `${o.description} (${o.ruleId})${o.count ? ` · ${o.count} rules` : ""}`
            }
            value={selectedCategoryOptions}
            onChange={onCategoryAutocompleteChange}
            isOptionEqualToValue={(o, v) => o.ruleId === v.ruleId}
            disabled={disabled}
            disableClearable={bypassAllCategories}
            sx={sigBypassMultiSelectAutocompleteSx}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const isAll = option.ruleId === SIG_ALL_CATEGORY_ID;
                const { key, ...tagProps } = getTagProps({ index });
                if (isAll) delete tagProps.onDelete;
                return (
                  <Chip
                    key={key || `sig_cat_chip_${option.ruleId}`}
                    {...tagProps}
                    label={
                      isAll
                        ? "All"
                        : `${option.description} (${option.ruleId})`
                    }
                    sx={{
                      fontSize: 12,
                      backgroundColor: colors.primary[300],
                      color: "#fff",
                      ...(!isAll && {
                        "& .MuiChip-deleteIcon": { color: "#fff" },
                        "& .MuiChip-deleteIcon:hover": { color: colors.neutral[200] },
                      }),
                    }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <SafeTextField
                {...params}
                label="Signature categories to bypass"
                placeholder=""
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              />
            )}
          />
        </GreenSelectForm>
      )}

      {/* Tier 2 — one Autocomplete per selected category (hidden when tier-1 is All) */}
      {tier2CategoryIds.length > 0 && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {tier2CategoryIds.map((catRuleId) => {
            const category = categoryOptions.find((c) => c.ruleId === catRuleId);
            const signaturesInCategory = secRulesByCategory[catRuleId] || [];
            const empty = signaturesInCategory.length === 0;
            const allSecOption = {
              sec_rule_id: SIG_ALL_SECRULE_ID,
              description: "All",
            };
            const secOptions = empty ? [] : [allSecOption, ...signaturesInCategory];
            const specificSelected = signaturesInCategory.filter((s) =>
              skipSecruleIds.includes(s?.sec_rule_id),
            );
            const value = specificSelected.length === 0 && !empty ? [allSecOption] : specificSelected;
            const bypassAllInCategory =
              value.length === 1 && value[0]?.sec_rule_id === SIG_ALL_SECRULE_ID;
            const labelTitle = category?.description || `Group ${catRuleId}`;
            return (
              <GreenSelectForm key={`sig_sec_pick_${catRuleId}`} variant="outlined" fullWidth>
                <Autocomplete
                  multiple
                  fullWidth
                  disableCloseOnSelect
                  disableClearable={bypassAllInCategory}
                  popupIcon={<ArrowdownIcon />}
                  options={secOptions}
                  getOptionLabel={(s) => {
                    if (s?.sec_rule_id === SIG_ALL_SECRULE_ID) return "All";
                    const sid = s?.sec_rule_id || "";
                    const desc = s?.description || s?.attack_type || "";
                    return desc ? `${sid} · ${desc}` : `${sid}`;
                  }}
                  value={value}
                  onChange={(_e, newVal) => onSecRulesAutocompleteChange(catRuleId, newVal)}
                  isOptionEqualToValue={(o, v) => o?.sec_rule_id === v?.sec_rule_id}
                  disabled={disabled || empty}
                  sx={sigBypassMultiSelectAutocompleteSx}
                  renderTags={(vals, getTagProps) =>
                    vals.map((option, index) => {
                      const isAll = option?.sec_rule_id === SIG_ALL_SECRULE_ID;
                      const { key, ...tagProps } = getTagProps({ index });
                      if (isAll) delete tagProps.onDelete;
                      return (
                        <Chip
                          key={key || `sig_sec_chip_${option?.sec_rule_id}`}
                          {...tagProps}
                          label={isAll ? "All" : option?.sec_rule_id}
                          sx={{
                            fontSize: 12,
                            backgroundColor: colors.neutral[200],
                            color: colors.neutral[1000],
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <SafeTextField
                      {...params}
                      label={`Specific ${labelTitle} signatures to bypass`}
                      placeholder={
                        empty ? "No individual signatures available for this category" : ""
                      }
                      InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    />
                  )}
                />
              </GreenSelectForm>
            );
          })}
        </Stack>
      )}

      {/* Bypass-all banner */}
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          minHeight: 48,
          p: 2,
          borderRadius: "8px",
          backgroundColor: bothEmpty ? "#FFF7E6" : colors.primary["alpha"],
          border: `solid 1px ${bothEmpty ? "#F0B429" : colors.primary[300]}`,
        }}
      >
        <Box sx={{ width: 24, height: 24 }}>
          <InfoIcon style={{ color: bothEmpty ? "#B7791F" : colors.primary[300], width: 24, height: 24 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 13,
            lineHeight: "20px",
            color: bothEmpty ? "#7A5400" : colors.primary[300],
          }}
        >
          {bothEmpty
            ? "All signature categories are selected — this exception bypasses every signature check for matching requests."
            : skipSecruleIds.length === 0
              ? `Bypassing ${skipCategories.length} categor${skipCategories.length === 1 ? "y" : "ies"} (all signatures in each).`
              : `Bypassing ${skipCategories.length} categor${skipCategories.length === 1 ? "y" : "ies"} + ${skipSecruleIds.length} individual signature${skipSecruleIds.length === 1 ? "" : "s"}.`}
        </Typography>
      </Stack>
    </Box>
  );
};

const ConfigWAFExceptionSettingsModal = ({ siteUid, open, handleClose, exceptionToEdit, canConfigureWAFForSite = true }) => {
  const { isAuthenticated, userType } = useAuth();
  const { getSitesForItems } = useSite();
  const {
    updateException,
    addNewException,
    setErr,
    errMsg,
    getExceptions,
    getCurrentException,
    getSdSigRules,
    getAllSdSecRules,
    sdSigRules,
    allSdSecRules,
  } = useWAFConfig();
  const { showSuccess, showError } = useNotification();
  const [exceptionName, setExceptionName] = useState("");
  const [conditions, setConditions] = useState([[{ key: "none", condition: "none", value: "" }]]);

  const [action, setAction] = useState(ExceptionSkipRuleType.ALL);
  // Signature WAF picker state.
  //   - skipCategories: array of category ruleId strings (e.g. "451").
  //   - skipSecruleIds: array of individual sec-rule IDs (e.g. "451100").
  // Both arrays empty + skip_rule_type=SENSEDEFENCE_SIGNATURE means
  // "bypass ALL signature checks for matching requests" (legacy semantic).
  const [skipCategories, setSkipCategories] = useState([]);
  const [skipSecruleIds, setSkipSecruleIds] = useState([]);
  const [conditionErrors, setConditionErrors] = useState([[{ key: false, condition: false, value: false }]]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [cellTouched, setCellTouched] = useState({});
  const [baselineKey, setBaselineKey] = useState(0);

  const touchCell = (i, j, field) => {
    setCellTouched((prev) => ({ ...prev, [`${i}.${j}.${field}`]: true }));
  };

  const isCellTouched = (i, j, field) => Boolean(cellTouched[`${i}.${j}.${field}`]);

  const showCellError = (i, j, field, errorMsg, andCond) =>
    resolveVisibleCellError(errorMsg, {
      touched: isCellTouched(i, j, field),
      submitAttempted,
      rowStarted: isExpressionRowStarted(andCond),
      valueEntered: field === "value" && String(andCond?.value ?? "").trim() !== "",
    });

  const [loading, setLoading] = React.useState(false);

  const formSnapshot = useMemo(
    () => ({
      exceptionName,
      conditions,
      action,
      skipCategories,
      skipSecruleIds,
    }),
    [exceptionName, conditions, action, skipCategories, skipSecruleIds],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, { disabled: loading });

  const formValidation = useMemo(
    () => validateExpressionRuleForm({ name: exceptionName, conditions }),
    [exceptionName, conditions],
  );

  const showNameErr = submitAttempted && formValidation.formErrors.name;
  const showConditionsErr = submitAttempted && formValidation.formErrors.conditions;

  const isSignatureAction =
    action === ExceptionSkipRuleType.SENSEDEFENCE_SIGNATURE ||
    action === ExceptionSkipRuleType.SIGNATURE;

  // Lazy-load the CRS rule catalog when the operator switches to a
  // signature-WAF skip-rule-type. Both endpoints are cheap (read-only
  // global catalog) and the context caches the result so a re-open
  // is instantaneous.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isSignatureAction) return;
    if (!sdSigRules) getSdSigRules();
    if (!allSdSecRules) getAllSdSecRules();
  }, [isAuthenticated, isSignatureAction, sdSigRules, allSdSecRules, getSdSigRules, getAllSdSecRules]);

  useEffect(() => {
    if (!open) return undefined;
    window.scrollTo(0, 0);
    const init = async () => {
      if (exceptionToEdit) {
        const detailedException = await getCurrentException(siteUid, exceptionToEdit?.id);
        setExceptionName(detailedException?.name ? detailedException?.name : undefined);
        setConditions(detailedException?.conditions ? detailedException?.conditions : [[{ key: "none", condition: "none", value: "" }]]);
        const conds = detailedException?.conditions ? detailedException?.conditions : [[{ key: "none", condition: "none", value: "" }]];
        setConditionErrors(conds.map((orCond) => orCond.map(() => ({ key: false, condition: false, value: false }))));
        const skipType = detailedException?.skip_rule_type;
        setAction(skipType === 1 ? ExceptionSkipRuleType.ALL : skipType ?? ExceptionSkipRuleType.ALL);
        setSkipCategories(Array.isArray(detailedException?.skip_categories) ? detailedException.skip_categories : []);
        setSkipSecruleIds(Array.isArray(detailedException?.skip_secrule_ids) ? detailedException.skip_secrule_ids : []);
      } else {
        setExceptionName("");
        setConditions([[{ key: "none", condition: "none", value: "" }]]);
        setConditionErrors([[{ key: false, condition: false, value: false }]]);
        setAction(ExceptionSkipRuleType.ALL);
        setSkipCategories([]);
        setSkipSecruleIds([]);
      }
      setSubmitAttempted(false);
      setCellTouched({});
      setBaselineKey((k) => k + 1);
    };
    if (isAuthenticated) init();
    return () => {
      setErr(null);
    };
  }, [open, exceptionToEdit, isAuthenticated, siteUid, setErr, getCurrentException]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getSitesForItems();
    }
    return () => setErr(null);
  }, [isAuthenticated, getSitesForItems, setErr]);

  const exceptionNameChange = (event) => {
    if (userType === UserType.ORG_USER) return;
    setExceptionName(event.target.value);
  };

  const selectKeyStr = (event, i, j) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list[i][j]["key"] = event.target.value;
    setConditions(list);
    const errList = [...conditionErrors];
    if (errList[i]?.[j]) errList[i][j].key = false;
    setConditionErrors(errList);
  };
  const selectCondStr = (event, i, j) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list[i][j]["condition"] = event.target.value;
    setConditions(list);
    const errList = [...conditionErrors];
    if (errList[i]?.[j]) errList[i][j].condition = false;
    setConditionErrors(errList);
  };
  const selectValueStr = (event, i, j) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list[i][j]["value"] = event.target.value;
    setConditions(list);
    const errList = [...conditionErrors];
    if (errList[i]?.[j]) errList[i][j].value = false;
    setConditionErrors(errList);
  };
  const selectCountryStr = (event, newValue, i, j) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list[i][j]["value"] = newValue?.value;
    setConditions(list);
    const errList = [...conditionErrors];
    if (errList[i]?.[j]) errList[i][j].value = false;
    setConditionErrors(errList);
  };

  const andClick = (i, j) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list[i].splice(j + 1, 0, { key: "none", condition: "none", value: "" });
    setConditions(list);
    const errList = [...conditionErrors];
    if (errList[i]) errList[i].splice(j + 1, 0, { key: false, condition: false, value: false });
    setConditionErrors(errList);
  };

  const orClick = () => {
    if (userType === UserType.ORG_USER) return;
    setConditions([...conditions, [{ key: "none", condition: "none", value: "" }]]);
    setConditionErrors([...conditionErrors, [{ key: false, condition: false, value: false }]]);
  };

  const removeClick = (i, j) => {
    if (userType === UserType.ORG_USER) return;
    let list = [...conditions];
    list[i].splice(j, 1);
    if (list[i].length === 0) list.splice(i, 1);
    if (0 === list.length) {
      list.push([{ key: "none", condition: "none", value: "" }]);
    }
    setConditions(list);
    const errList = [...conditionErrors];
    if (errList[i]) {
      errList[i].splice(j, 1);
      if (errList[i].length === 0) errList.splice(i, 1);
      if (0 === errList.length) errList.push([{ key: false, condition: false, value: false }]);
      setConditionErrors(errList);
    }
  };

  const handleSave = async () => {
    if (!canConfigureWAFForSite) return;
    if (userType === UserType.ORG_USER) return;
    setSubmitAttempted(true);
    const validation = validateExpressionRuleForm({ name: exceptionName, conditions });
    if (!validation.valid) {
      setConditionErrors(validation.conditionErrors);
      return;
    }
    setLoading(true);
    try {
      let result = {};
      const payload = {
        name: exceptionName,
        conditions: conditions,
        skip_rule_type: action,
        skip_secrule_ids: isSignatureAction ? skipSecruleIds : [],
        skip_categories: isSignatureAction ? skipCategories : [],
      };
      if (exceptionToEdit) {
        result = await updateException(siteUid, exceptionToEdit?.id, payload);
      } else {
        result = await addNewException(siteUid, payload);
      }
      if (result?.status === "success") {
        showSuccess(result?.msg);
        getExceptions(siteUid);
        handleClose();
      } else {
        showError(result?.msg);
      }
    } catch (err) {
      showError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (errMsg) {
      showError(errMsg);
    }
  }, [errMsg, showError]);

  return (
    <>
      <Modal 
        open={open} 
        onClose={requestClose}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
          }
        }}
      >
        <Slide direction="left" in={open} mountOnEnter unmountOnExit>
          <Stack
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 480,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
            }}
          >
            <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "8px",
                    backgroundColor: "#EEF0FE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ExceptIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
                </Box>
                <Stack>
                  <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                    <Typography
                      sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                    >
                      {exceptionToEdit ? "Edit WAF Exception" : "Add WAF Exception"}
                    </Typography>
                    <FormHelpTooltip tooltip="Skip WAF inspection for requests matching URL, method, header, or parameter expressions. Use for trusted callbacks, webhooks, or paths that false-positive." />
                  </Stack>
                  <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                    Create a rule to bypass WAF protection for trusted requests.
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
                <CloseIcon sx={{ color: colors.neutral[600] }} />
              </IconButton>
            </Stack>
            <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
              <ReactPerfectScrollbar>
                <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                  <Stack spacing={4}>
                    <SafeTextField
                      fullWidth
                      label="Exception name"
                      value={exceptionName}
                      onChange={exceptionNameChange}
                      error={Boolean(showNameErr)}
                      helperText={showNameErr || ""}
                    />
                    {conditions.map((orCond, i) => {
                      return (
                        <Box
                          key={`cond_${i}`}
                          sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}
                        >
                          <Grid container spacing={3}>
                            {orCond.map((andCond, j) => {
                              const cellErr = formValidation.conditionErrors[i]?.[j] || {};
                              const keyErr = showCellError(i, j, "key", cellErr.key, andCond);
                              const condErr = showCellError(i, j, "condition", cellErr.condition, andCond);
                              const valueErr = showCellError(i, j, "value", cellErr.value, andCond);
                              const valueHint = getExpressionValueHint(andCond.key);
                              return (
                                <>
                                  <Grid item xs={12}>
                                    <Typography sx={ { fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                                      {j === 0 ? (i === 0 ? "When incoming requests match..." : "Or") : "And"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container spacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        <Box onBlur={() => touchCell(i, j, "key")}>
                                          <KeyComponent
                                            keyStr={andCond.key}
                                            selectKeyStr={(event) => {
                                              touchCell(i, j, "key");
                                              selectKeyStr(event, i, j);
                                            }}
                                            padx={i}
                                            pady={j}
                                            error={keyErr}
                                          />
                                          <ExpressionFieldMessage error={keyErr} />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        <Box onBlur={() => touchCell(i, j, "condition")}>
                                          <CondComponent
                                            condStr={andCond.condition}
                                            selectCondStr={(event) => {
                                              touchCell(i, j, "condition");
                                              selectCondStr(event, i, j);
                                            }}
                                            disableMenu={getValidCondFlag(andCond.key)}
                                            disabled={andCond.key === "none"}
                                            padx={i}
                                            pady={j}
                                            error={condErr}
                                          />
                                          <ExpressionFieldMessage error={condErr} />
                                        </Box>
                                      </Grid>
                                      <Grid item xs={12}>
                                        {andCond.key === ExpressionKeyField.METHOD ? (
                                          <Box onBlur={() => touchCell(i, j, "value")}>
                                            <MethodValueComponent
                                              valueStr={andCond.value}
                                              selectValueStr={(event) => {
                                                touchCell(i, j, "value");
                                                selectValueStr(event, i, j);
                                              }}
                                              disabled={andCond.key === ExpressionKeyField.NONE || andCond.condition === "none"}
                                              padx={i}
                                              pady={j}
                                              error={valueErr}
                                            />
                                            <ExpressionFieldMessage error={valueErr} hint={valueHint} />
                                          </Box>
                                        ) : andCond.key === ExpressionKeyField.COUNTRY ? (
                                          <Box onBlur={() => touchCell(i, j, "value")}>
                                            <CountryValueComponent
                                              valueStr={andCond.value}
                                              selectValueStr={(event, newValue) => {
                                                touchCell(i, j, "value");
                                                selectCountryStr(event, newValue, i, j);
                                              }}
                                              disabled={andCond.key === "none" || andCond.condition === "none"}
                                              error={valueErr}
                                            />
                                            <ExpressionFieldMessage error={valueErr} hint={valueHint} />
                                          </Box>
                                        ) : (
                                          <ValueComponent
                                            keyStr={andCond.key}
                                            valueStr={andCond.value}
                                            selectValueStr={(event) => {
                                              touchCell(i, j, "value");
                                              selectValueStr(event, i, j);
                                            }}
                                            onBlur={() => touchCell(i, j, "value")}
                                            disabled={andCond.key === ExpressionKeyField.NONE || andCond.condition === "none"}
                                            padx={i}
                                            pady={j}
                                            error={valueErr}
                                            helperText={valueHint}
                                          />
                                        )}
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Stack width={"100%"} alignItems={"center"} direction="row" justifyContent="end" spacing={4}>
                                          <Stack
                                            direction={"row"}
                                            alignItems={"center"}
                                            spacing={1}
                                            onClick={() => andClick(i, j)}
                                            sx={{
                                              cursor: "pointer",
                                              borderRadius: "8px",
                                              border: `solid 1px ${colors.secondary[300]}`,
                                              px: 2,
                                              py: 1,
                                            }}
                                          >
                                            <DuplicateIcon style={{ color: colors.neutral[800] }} />
                                            <Typography
                                              sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}
                                            >
                                              And
                                            </Typography>
                                          </Stack>
                                          <Box onClick={() => removeClick(i, j)} sx={{ cursor: "pointer" }}>
                                            <DeleteIcon style={{ color: colors.neutral[600] }} />
                                          </Box>
                                        </Stack>
                                      </Grid>
                                      {conditions.length - 1 === i && orCond.length - 1 === j && (
                                        <Grid item xs={12}>
                                          <Button
                                            startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
                                            onClick={orClick}
                                            sx={{ height: 32, width: 105, ...containedButtonSx }}
                                          >
                                            Add new
                                          </Button>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                </>
                              );
                            })}
                          </Grid>
                        </Box>
                      );
                    })}
                    {showConditionsErr && (
                      <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: -2 }}>
                        {formValidation.formErrors.conditions}
                      </Typography>
                    )}
                    <GreenSelectForm variant="outlined" fullWidth>
                      <InputLabel id="waf-engine-information">WAF Engine Selection</InputLabel>
                      <Select
                        fullwidth
                        IconComponent={ArrowdownIcon}
                        labelId="waf-engine-information"
                        value={action}
                        onChange={(event) => {
                          setAction(event.target.value);
                          setSkipCategories([]);
                          setSkipSecruleIds([]);
                        }}
                      >
                        <MenuItem key={"action_0"} value={ExceptionSkipRuleType.ALL} sx={menuItemSx}>
                          Global
                        </MenuItem>
                        <MenuItem key={"action_1"} value={ExceptionSkipRuleType.SMART_PARSE} sx={menuItemSx}>
                          Smart Parse WAF
                        </MenuItem>
                        <MenuItem key={"action_3"} value={ExceptionSkipRuleType.SENSEDEFENCE_SIGNATURE} sx={menuItemSx}>
                          Layerd Signature WAF
                        </MenuItem>
                      </Select>
                    </GreenSelectForm>
                    {isSignatureAction && (
                      <SignatureBypassPicker
                        sdSigRules={sdSigRules}
                        allSdSecRules={allSdSecRules}
                        skipCategories={skipCategories}
                        setSkipCategories={setSkipCategories}
                        skipSecruleIds={skipSecruleIds}
                        setSkipSecruleIds={setSkipSecruleIds}
                        disabled={userType === UserType.ORG_USER}
                      />
                    )}
                    <Stack spacing={4}>
                      <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                          minHeight: 48,
                          p: 2,
                          borderRadius: "8px",
                          backgroundColor: colors.primary["alpha"],
                          border: `solid 1px ${colors.primary[300]}`,
                        }}
                      >
                        <Box sx={{ width: 24, height: 24 }}>
                          <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                        </Box>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                          You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                  <Stack direction={"row"} alignItems={"center"} spacing={3}>
                    <SaveButton onClick={handleSave} disabled={loading || !canConfigureWAFForSite} sx={formSaveButtonSx} />
                    <CancelButton onClick={requestClose} />
                  </Stack>
                </Stack>
              </ReactPerfectScrollbar>
            </Box>
          </Stack>
        </Slide>
      </Modal>
      {confirmDialog}
    </>
  );
};
export default ConfigWAFExceptionSettingsModal;
